import React, { useMemo } from 'react';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, injectIntl, IntlShape, MessageDescriptor } from 'react-intl';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import DefaultButton from '../../../../../global/default-button/default-button';
import { FormField } from '../../../../../global/form/form-field/form-field';
import WebsiteInput from '../../../../../global/website-input/website-input';
import { getWebsiteFieldValidationError } from '../../../../../../infrastructure/utils/getWebsiteFieldValidationError';
import { EnablementStatus, OPTION_ELIPSIS_MAX_LENGTH } from '../../../../../../constants/common.constants';
import { ONLY_SPACES_REG_EXP } from '../../../../../../constants/regex.constants';
import { RichTextEditor } from '../../../../../global/rich-text-editor';
import { OERadioOptions } from '../../../../../global/oe-radio-options/oe-radio-options';
import { MagicLinkValidityPeriodStrategy } from '../../../../../../api/website.dao';
import Text from '../../../../../global/text/text';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import { WebsitePrimaryColor } from '../website-primary-color';
import './passport-settings-form.scss';
import { UploadImageS3Presentation } from '../../../shared/upload-image/upload-image-to-s3.presentation';
import PassportSettingsFormVM from './passport-settings-form.viewmodel';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { getFormattedTimezoneOffsetByTimezone, getFullFormattedTimezoneOffset } from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { accessCodeStatus, calendarInvitesOptions, signInLandings, timeZoneDisplayTypeLabels } from './passport-settings.constants';
import {
    APPLY_AND_CLOSE_TEXT,
    APPLY_TEXT,
    CANCEL_TEXT,
    DEFAULT_TEXT,
    EVENT_PRIVATE_DESCRIPTION_TEXT,
    EVENT_PUBLIC_DESCRIPTION_TEXT,
    EVENT_TITLE_FOR_PASSPORT_TEXT,
    LOGO_IMAGE_TEXT,
    loadingText
} from '../../../../../../constants/common-labels.constants';
import {
    BACKGROUND_IMAGE_ALT_TEXT,
    CALENDAR_INVITES_INFO_TEXT,
    COOKIE_NOTIFICATION_TEXT,
    CUSTOM_TIME_ZONE_TEXT,
    LOGO_IMAGE_ALT_TEXT,
    MENU_BAR_LOGO_IMAGE_ALT_TEXT,
    DATA_IS_BEING_SAVED_TEXT,
    DAYS_TEXT,
    EVENT_TIMEZONE_FOR_MEETINGS_INFO_TEXT,
    EVENT_TIMEZONE_FOR_MEETINGS_TEXT,
    FIELD_LABEL_TEXT,
    FOOTER_BAR_LOGO_IMAGE_TEXT,
    IMAGE_BACKGROUND_TEXT,
    MAGIC_LINK_VALIDATION_PERIOD_TEXT,
    MAGIC_LINKS_VALID_FOR24_HOURS_TEXT,
    MAGIC_LINKS_VALID_UNTIL_EVENT_END_DATE_TEXT,
    MENU_BAR_LOGO_IMAGE_TEXT,
    ONE_STEP_REQUEST_ACCESS_CODE_TEXT,
    PRIVACY_POLICY_CHECKBOX_TEXT,
    SIGN_IN_LANDING_WITH_TEXT,
    TIME_ZONE_DISPLAY_THEME_TEXT,
    TOGGLE_COOKIE_NOTIFICATION_TEXT,
    WEBSITE_PASSPORT_THEME_TEXT,
    PASSPORT_URL_TEXT,
    ATTENDEE_SSO_TEXT,
    ATTENDEE_SSO_INFO_TEXT,
    CONTROLLED_AT_ACCOUNT_LEVEL_TEXT
} from './passport-settings-form.texts';
import { GeneratePassportBackgroundImageWithAI } from './components/upload-image-to-s3-ai/upload-image-to-s3-ai.presentation';
import { UploadImageS3WithAIViewModel } from './components/upload-image-to-s3-ai/upload-image-to-s3-ai.viewmodel';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';

interface IPassportSettingsFormPresentation {
    vm: PassportSettingsFormVM;
    intl: IntlShape;
}

const PassportSettingsFormPresentation = injectIntl(mobxReact.observer(({ vm, intl }: IPassportSettingsFormPresentation) => {
    const globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage);
    const timeZone = useMemo(() => globalStorage.getEvent().timeZone, [globalStorage.getEvent()]);
    const isGeneratePassportBackgroundAllowed = hasPermission(Permission.GENERATE_IMAGES_WITH_AI);

    if (vm.isLoading) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    {loadingText()}
                </div>
            }
        />;
    }

    const optionElipsis = (value: string): string => {
        return value.length > OPTION_ELIPSIS_MAX_LENGTH ? value.slice(0, OPTION_ELIPSIS_MAX_LENGTH) + ' ...' : value;
    };

    const removeOnlySpacesFromField = mobx.action((value: string, key: string) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            vm[key] = '';
        }
    });

    const {
        uploadLogoImageS3VM,
        uploadBackgroundImageS3VM,
        uploadWebsiteLogoS3VM,
        uploadFooterLogoS3VM,
    } = vm;

    const getTimeZoneDisplayType = (value: string, name: MessageDescriptor): string => {
        switch (value) {
            case timeZoneDisplayTypeLabels.default.value:
                return `${getFormattedTimezoneOffsetByTimezone(timeZone)}`;
            case timeZoneDisplayTypeLabels.offsetWithGeography.value:
                return `${intl.formatMessage(name)}: ${getFullFormattedTimezoneOffset(timeZone)}`;
            case timeZoneDisplayTypeLabels.custom.value:
            default:
                return intl.formatMessage(name);
        }
    };

    return (
        <CommonPopupBodyFooterLayout
            body={
                <>
                    <div className='passport-settings'>
                        <div className='oe-form'>
                            <FormField label={<FormattedMessage {...PASSPORT_URL_TEXT} />}>
                                <WebsiteInput
                                    onChange={e => vm.setSubdomain(e.target.value.trim())}
                                    value={vm.subdomain}
                                    accountSubdomain={vm.getAccountSubdomain()}
                                    className='edit-passport-website-input'
                                />
                                {getWebsiteFieldValidationError(vm.subdomain) && <ul className='oe-form-field__list-error'>
                                    {getWebsiteFieldValidationError(vm.subdomain).map((x, i) => <li key={i}>{x}</li>)}
                                </ul>}
                            </FormField>

                            <FormField label={<FormattedMessage {...WEBSITE_PASSPORT_THEME_TEXT} />} >
                                <select
                                    value={vm.activeThemeId || ''}
                                    onChange={e => {
                                        const value = e.target.value !== '' ? + e.target.value : null;
                                        vm.setTheme(value);
                                    }}
                                    className='oe-form-field__control'
                                    disabled={!vm.themeList.length || !vm.subdomain}
                                >
                                    <option value=''>
                                        {intl.formatMessage(DEFAULT_TEXT)}
                                    </option>
                                    {vm.themeList?.map(({ id, name }, i) => <option value={id} key={i}>
                                        {optionElipsis(name)}
                                    </option>)}
                                </select>
                            </FormField>
                            <FormField label={<FormattedMessage {...EVENT_TITLE_FOR_PASSPORT_TEXT} />} >
                                <textarea
                                    rows={2}
                                    className='oe-form-field__control passport-form__textarea'
                                    value={vm.eventTitleForPassport}
                                    onChange={({ target: { value } }) => vm.setEventTitleForPassport(value)}
                                    onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'eventTitleForPassport'))}
                                />
                                {vm.getEventTitleForPassportError() && <span className='oe-form-field__error'>{vm.getEventTitleForPassportError()}</span>}
                            </FormField>

                            <FormField
                                label={<FormattedMessage {...EVENT_PUBLIC_DESCRIPTION_TEXT} />}
                                className='website-custom__editor'
                            >
                                <RichTextEditor
                                    resizable
                                    value={vm.eventPublicDescription}
                                    onChange={mobx.action((event, editor) => vm.setPublicDescription(editor.getData()))}
                                    validationError={vm.getPublicDescriptionError()}
                                />
                            </FormField>
                            <FormField
                                label={
                                    <FormattedMessage {...EVENT_PRIVATE_DESCRIPTION_TEXT} />}
                                className='website-custom__editor'
                            >
                                <RichTextEditor
                                    resizable
                                    value={vm.eventPrivateDescription}
                                    onChange={mobx.action((event, editor) => vm.setPrivateDescription(editor.getData()))}
                                    validationError={vm.getPrivateDescriptionError()}
                                />
                            </FormField>
                            <FormField label={<FormattedMessage {...TIME_ZONE_DISPLAY_THEME_TEXT} />}>
                                <select
                                    value={vm.timeZoneDisplayType}
                                    onChange={e => vm.setTimeZoneDisplayType(e.target.value)}
                                    className='oe-form-field__control'
                                >
                                    {Object.values(timeZoneDisplayTypeLabels).map(({ value, name }, i) =>
                                        <option value={value}
                                            key={i}>{optionElipsis(getTimeZoneDisplayType(value, name))} </option>)}
                                </select>
                            </FormField>
                            {vm.timeZoneDisplayType === timeZoneDisplayTypeLabels.custom.value &&
                                <FormField label={<FormattedMessage {...CUSTOM_TIME_ZONE_TEXT} />} >
                                    <div className='flex'>
                                        <input
                                            className='oe-form-field__control'
                                            onChange={({ target: { value } }) => vm.setCustomTimeZoneDisplay(value)}
                                            value={vm.customTimeZoneDisplay}
                                            type='text'
                                        />
                                    </div>
                                    {vm.getCustomTimeZoneDisplayError() &&
                                        <span className='oe-form-field__error'>{vm.getCustomTimeZoneDisplayError()}</span>}
                                </FormField>
                            }
                            <div className='website-custom__checkbox-label '>
                                <OEToggle
                                    isSelected={vm.showEventTimeZone}
                                    onClick={vm.toggleShowEventTimeZone}
                                    isDisabled={false}
                                />
                                <Text
                                    {...EVENT_TIMEZONE_FOR_MEETINGS_TEXT}
                                    values={{
                                        suffix: vm.showEventTimeZone ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                                <InfoPopover>
                                    <FormattedMessage
                                        {...EVENT_TIMEZONE_FOR_MEETINGS_INFO_TEXT}
                                    />
                                </InfoPopover>
                            </div>

                            {vm.canViewAttendeeSsoSection() && <div>
                                <div className={`website-custom__checkbox-label ${vm.getisAttendeeSSOLocked() && 'locked-idp p-left-5px'}`}>
                                    <OEToggle
                                        isSelected={vm.getisAttendeeSSOLocked() ? true : !!vm.attendeeSSOEnabled}
                                        onClick={vm.toggleAttendeeSSO}
                                        isDisabled={!hasPermission(Permission.ATTENDEE_SSO) || vm.getisAttendeeSSOLocked()} />
                                    <Text
                                        className={`${vm.getisAttendeeSSOLocked() && 'width-45'}`}
                                        {...ATTENDEE_SSO_TEXT}
                                        values={{
                                            suffix: vm.attendeeSSOEnabled || vm.getisAttendeeSSOLocked() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                        }} />
                                    {!vm.getisAttendeeSSOLocked() && <InfoPopover className='m-left-10px'>
                                        <FormattedMessage {...ATTENDEE_SSO_INFO_TEXT} />
                                    </InfoPopover>}
                                    {vm.getisAttendeeSSOLocked() &&
                                        <Text
                                            className='control-text m-left-14px'
                                            {...CONTROLLED_AT_ACCOUNT_LEVEL_TEXT}
                                        />
                                    }
                                </div>
                                {(vm.attendeeSSOEnabled || vm.getisAttendeeSSOLocked() ) && <div className='idp-configuration-area'>
                                    {vm.selectedIdps?.map(provider => {
                                        return (
                                            <div
                                                key={provider.fusionAuthIdpId}
                                                className={`idp-row flex center m-bottom-5px ${vm.getisIdpLocked(provider.fusionAuthIdpId) && 'locked-idp'}`}
                                            >
                                                <OEToggle
                                                    isSelected={vm.getisIdpLocked(provider.fusionAuthIdpId) ? true : !!provider.enabled}
                                                    onClick={() => vm.toggleSelectedProvider(provider.fusionAuthIdpId)}
                                                    isDisabled={!hasPermission(Permission.ATTENDEE_SSO) || vm.getisIdpLocked(provider.fusionAuthIdpId)}
                                                />
                                                <Text
                                                    className="idp-name"
                                                    message={provider.fusionAuthIdpName}
                                                    withEllipsis
                                                />
                                                {vm.getisIdpLocked(provider.fusionAuthIdpId) &&
                                                    <Text
                                                        className='control-text m-left-15px'
                                                        {...CONTROLLED_AT_ACCOUNT_LEVEL_TEXT}
                                                        withEllipsis
                                                    />
                                                }
                                            </div>
                                        );
                                    })}
                                </div>}
                                {vm.idpConfigurationValidationError && <span className='oe-form-field__error'>{vm.idpConfigurationValidationError}</span>}
                            </div>}

                            <FormField label={<FormattedMessage {...SIGN_IN_LANDING_WITH_TEXT} />}>
                                <select
                                    value={vm.signInLandingWith}
                                    onChange={e => vm.setSignInLanding(e.target.value)}
                                    className='oe-form-field__control'
                                >
                                    {Object.values(signInLandings).map(({ value, name }, i) =>
                                        <option value={value} key={i}>{intl.formatMessage(name)}</option>)
                                    }
                                </select>
                            </FormField>

                            <div className='website-custom__checkbox-label '>
                                <OEToggle
                                    isSelected={vm.privacyPolicyEnabled}
                                    onClick={vm.toggleSetPrivacyPolicy}
                                    isDisabled={false}
                                />
                                <Text
                                    {...PRIVACY_POLICY_CHECKBOX_TEXT}
                                    values={{
                                        suffix: vm.privacyPolicyEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>

                            {vm.privacyPolicyEnabled &&
                                <FormField
                                    label={<FormattedMessage {...FIELD_LABEL_TEXT} />}
                                    className='website-custom__editor'
                                >
                                    <RichTextEditor
                                        value={vm.privacyPolicyLabel}
                                        onChange={mobx.action((event, editor) => vm.setPrivacyPolicyLabel(editor.getData()))}
                                        validationError={vm.getPrivacyPolicyLabelError()}
                                    />
                                </FormField>
                            }

                            {/* Check this */}
                            <FormField label={<FormattedMessage id='oe.access.code' />}>
                                <select
                                    value={vm.accessCodeStatus}
                                    onChange={e => vm.setAccessCodeStatus(e.target.value)}
                                    className='oe-form-field__control'
                                >
                                    {Object.values(accessCodeStatus).map(({ value, name }, i) => <option value={value}
                                        key={i}>{intl.formatMessage(name)}</option>)}
                                </select>
                            </FormField>
                            {vm.accessCodeStatus === accessCodeStatus.predefined.value &&
                                // check this
                                <FormField label={<FormattedMessage id='oe.website.predefinedAccessCode' />}>
                                    <div className='flex'>
                                        <input
                                            className='oe-form-field__control'
                                            onChange={({ target: { value } }) => vm.setPredefinedAccessCode(value)}
                                            onBlur={({ target: { value } }) => vm.setPredefinedAccessCode(value)}
                                            value={vm.predefinedAccessCode}
                                            type='text'
                                        />
                                    </div>
                                    {vm.predefinedAccessCodeValidationError &&
                                        <span className='oe-form-field__error'>{vm.predefinedAccessCodeValidationError}</span>}
                                </FormField>
                            }

                            <div className='website-custom__checkbox-label m-bottom-0px'>
                                <OEToggle
                                    isSelected={vm.requestAccessCodeAutoSingInEnabled}
                                    onClick={vm.toggleRequestAccessCodeAutoSingIn}
                                    isDisabled={false}
                                />
                                <Text {...ONE_STEP_REQUEST_ACCESS_CODE_TEXT} />
                            </div>
                            <span className='oe-form-field__info m-bottom-15px'>
                                <FormattedMessage id='oe.websiteTab.oneStepRequestAccessCode.info' />
                            </span>

                            <FormField
                                label={<FormattedMessage id='oe.calendar.invites' />}
                                infoIconText={intl.formatMessage(CALENDAR_INVITES_INFO_TEXT)}
                                className='m-bottom-20px'
                            >
                                <select
                                    value={vm.selectedCalendarInvite}
                                    onChange={e => vm.setCalendarInviteStatus(e.target.value)}
                                    className='oe-form-field__control'
                                >
                                    {Object.values(calendarInvitesOptions).map(({ value, name }, i) => <option value={value}
                                        key={i}>{intl.formatMessage(name)}</option>)}
                                </select>
                            </FormField>

                            <div className='website-custom__checkbox-label m-bottom-20px'>
                                <OEToggle
                                    isSelected={vm.showCalendarButton}
                                    onClick={vm.toggleShowCalendarButton}
                                    isDisabled={false}
                                />
                                <Text
                                    id='oe.website.showCalendarButton'
                                    message='Calendar button on meeting details page {suffix}'
                                    values={{
                                        suffix: vm.showCalendarButton ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>

                            <FormField label={<FormattedMessage {...MAGIC_LINK_VALIDATION_PERIOD_TEXT} />}>
                                <OERadioOptions
                                    width='fit-content'
                                    options={[
                                        {
                                            label: intl.formatMessage(MAGIC_LINKS_VALID_FOR24_HOURS_TEXT),
                                            value: MagicLinkValidityPeriodStrategy.CURRENT_DATE
                                        },
                                        {
                                            content: (
                                                <div className='oe-radio-option__content'>
                                                    <div>
                                                        <Text
                                                            className='oe-radio-option__label-text'
                                                            {...MAGIC_LINKS_VALID_UNTIL_EVENT_END_DATE_TEXT}
                                                        />
                                                        <div className='passport-settings__validity-days'>
                                                            <input
                                                                className='oe-form-field__control m-left-4px m-right-4px w-fit-content'
                                                                type='number'
                                                                min={0}
                                                                max={99}
                                                                disabled={vm.magicLinkBuildStrategy === MagicLinkValidityPeriodStrategy.CURRENT_DATE}
                                                                value={vm.magicLinkValidityDays}
                                                                onChange={vm.setMagicLinkValidityDays}
                                                            />
                                                            <Text
                                                                className='oe-radio-option__label-text'
                                                                {...DAYS_TEXT}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ),
                                            value: MagicLinkValidityPeriodStrategy.EVENT_END_DATE
                                        }
                                    ]}
                                    value={vm.magicLinkBuildStrategy}
                                    onOptionToggle={vm.setMagicLinkValidityPeriodStrategy}
                                />
                                {vm.getMagicLinkValidityDaysValidationError() &&
                                    <span className='oe-form-field__error'>{vm.getMagicLinkValidityDaysValidationError()}</span>}
                            </FormField>

                            <UploadImageS3Presentation
                                vm={uploadWebsiteLogoS3VM}
                                fieldName={intl.formatMessage(MENU_BAR_LOGO_IMAGE_TEXT)}
                            />
                            <FormField label={<FormattedMessage {...MENU_BAR_LOGO_IMAGE_ALT_TEXT} />}>
                                <input
                                    className='oe-form-field__control'
                                    type='text'
                                    value={vm.websiteLogoImageAltText}
                                    onChange={({ target: { value } }) => vm.setWebsiteLogoImageAltText(value)}
                                />
                                {vm.getWebsiteLogoImageAltTextError() &&
                                    <span className='oe-form-field__error'>{vm.getWebsiteLogoImageAltTextError()}</span>}
                            </FormField>

                            <UploadImageS3Presentation
                                vm={uploadLogoImageS3VM}
                                fieldName={intl.formatMessage(LOGO_IMAGE_TEXT)}
                            />
                            <FormField label={<FormattedMessage {...LOGO_IMAGE_ALT_TEXT} />}>
                                <input
                                    className='oe-form-field__control'
                                    type='text'
                                    value={vm.logoImageAltText}
                                    onChange={({ target: { value } }) => vm.setLogoImageAltText(value)}
                                />
                                {vm.getLogoImageAltTextError() &&
                                    <span className='oe-form-field__error'>{vm.getLogoImageAltTextError()}</span>}
                            </FormField>

                            {isGeneratePassportBackgroundAllowed
                                ? <GeneratePassportBackgroundImageWithAI
                                    vm={uploadBackgroundImageS3VM as UploadImageS3WithAIViewModel}
                                    fieldName={intl.formatMessage(IMAGE_BACKGROUND_TEXT)}
                                />
                                : <UploadImageS3Presentation
                                    vm={uploadBackgroundImageS3VM}
                                    fieldName={intl.formatMessage(IMAGE_BACKGROUND_TEXT)}
                                />
                            }
                            <FormField label={<FormattedMessage {...BACKGROUND_IMAGE_ALT_TEXT} />}>
                                <input
                                    className='oe-form-field__control'
                                    type='text'
                                    value={vm.backgroundImageAltText}
                                    onChange={({ target: { value } }) => vm.setBackgroundImageAltText(value)}
                                />
                                {vm.getBackgroundImageAltTextError() &&
                                    <span className='oe-form-field__error'>{vm.getBackgroundImageAltTextError()}</span>}
                            </FormField>
                            <UploadImageS3Presentation
                                vm={uploadFooterLogoS3VM}
                                fieldName={intl.formatMessage(FOOTER_BAR_LOGO_IMAGE_TEXT)}
                            />
                            <div className='m-bottom-25px'>
                                <WebsitePrimaryColor
                                    onChange={e => vm.setPrimaryColor(e.target.value)}
                                    value={vm.primaryColor}
                                    normalizedValue={vm.getNormalizedPrimaryColor()}
                                    validationError={vm.getColorValidationError()}
                                />
                            </div>

                            <div className='website-custom__checkbox-label m-bottom-15px'>
                                <OEToggle
                                    isSelected={vm.showCookieNotification}
                                    onClick={vm.toggleShowCookieNotification}
                                />
                                <Text {...TOGGLE_COOKIE_NOTIFICATION_TEXT} />
                            </div>

                            {vm.showCookieNotification && <FormField
                                label={<FormattedMessage {...COOKIE_NOTIFICATION_TEXT} />}
                                className='website-custom__editor'
                            >
                                <RichTextEditor
                                    value={vm.cookieNotificationText}
                                    onChange={mobx.action((event, editor) => vm.setCookieNotificationText(editor.getData()))}
                                    validationError={vm.getCookieNotificationTextError()}
                                />
                            </FormField>}

                        </div>
                    </div>
                </>
            }
            footer={
                <>
                    {vm.isSaving && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                {...DATA_IS_BEING_SAVED_TEXT}
                                values={{
                                    suffix: '...'
                                }}
                            />

                        </span>
                    )}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(APPLY_AND_CLOSE_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => vm.applyPassportSettingsForm()}
                            disabled={vm.isImageUploading()}
                        />
                        <DefaultButton
                            text={intl.formatMessage(APPLY_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => vm.applyPassportSettingsForm()}
                            disabled={vm.isImageUploading()}
                        />
                        <DefaultCloseSubWindowButton text={intl.formatMessage(CANCEL_TEXT)} />
                    </div>
                </>
            }
        />
    );
}));


export { PassportSettingsFormPresentation };
